import {
  oneWords,
  ROOT,
  textWords,
  type WizardFieldErrors,
  type WizardRefusalWording,
} from "@/base";
import {
  formatAddress,
  EMPTY_ADDRESS,
  isCompleteAddress,
  trimAddress,
  type AddressValue,
} from "@/lib/address";
import type { AccountDetail, Address } from "@/lib/brokerAccounts";
import type {
  CityLimits,
  CodeDisplayRef,
  CodeOption,
  CodeWage,
  PremisesInterest,
  SubmissionLocation,
  SubmissionLocationInput,
} from "@/lib/submissions/brokerSubmissions";

// The WC wizard's Locations and payroll step (BRK-S302), as pure rules. Each
// location holds its own class-code rows with full-time, part-time and payroll,
// the way the ACORD 130 and Great American's application group them. PMS keeps
// the rows flat (`exposures`, each naming its `locationId`), so the step
// converts both ways. Locations stay on the submission until bind (PMS-S702,
// Nav 2026-10-07); the account's premises change only then (PMS-S713).

/** Why a row sits on location 1 until the broker checks it: PMS held it with
 *  no location, or one that isn't the submission's (`saved`); or an uploaded
 *  document didn't say which of its locations the row is at (`document`,
 *  BRK-S306: extraction names it only as text). */
export type CheckLocation = "saved" | "document";

/** One class-code row at a location: an exposure, in PMS's words. */
export interface WcClassRow {
  key: string;
  code: CodeOption | null;
  fullTime: number | undefined;
  partTime: number | undefined;
  payroll: number | undefined;
  /** The stored row's other fields, written back as they came. */
  carried: Record<string, unknown>;
  checkLocation?: CheckLocation;
  /** The class code an uploaded document printed when PMS couldn't match it
   *  to one (BRK-S306): the broker picks the match. */
  documentCode?: string;
}

/** The building details a location's toggle opens. */
export interface WcBuilding {
  bldNumber: string;
  yearBuilt: number | undefined;
  /** Square feet. */
  occupiedArea: number | undefined;
  interest: PremisesInterest | "";
  cityLimits: CityLimits | "";
  highestFloor: number | undefined;
}

/** A location's building details, as the step asks them and Review shows
 *  them. */
export const PREMISES_INTEREST_OPTIONS: {
  value: PremisesInterest;
  label: string;
}[] = [
  { value: "owner", label: "Owner" },
  { value: "tenant", label: "Tenant" },
];
export const CITY_LIMITS_OPTIONS: { value: CityLimits; label: string }[] = [
  { value: "inside", label: "Inside city limits" },
  { value: "outside", label: "Outside city limits" },
];

export interface WcLocation {
  key: string;
  /** Its id on the submission, once PMS has kept it. */
  id?: string;
  /** The account address it was copied from. */
  sourceLocationId?: string;
  /** PMS's location number, carried as it came. */
  locNumber?: string;
  address: AddressValue;
  /** The "Add building details?" toggle: null is unanswered, not No. */
  hasBuildingDetails: boolean | null;
  building: WcBuilding;
  rows: WcClassRow[];
}

/**
 * Where the mailing address comes from (BRK-S390): one of the entered
 * locations, by its key (location 1's by default, and again should the one
 * chosen go), or, with `location: null`, a separate address.
 */
export interface WcMailing {
  location: string | null;
  mailing?: AddressValue;
}

/** A new application's first location's key: the mailing address's default
 *  until the step reads what PMS holds. */
export const FIRST_LOCATION_KEY = "location-1";

/** The mailing select's value for "Enter a different address". */
export const MAILING_ELSEWHERE = "elsewhere";

/** What a line's Locations step asks beyond the addresses and payroll. */
export interface LocationsAsks {
  /** Workers' Comp doesn't ask building details (BRK-S390); BOP will. */
  buildings: boolean;
}

export const EMPTY_BUILDING: WcBuilding = {
  bldNumber: "",
  yearBuilt: undefined,
  occupiedArea: undefined,
  interest: "",
  cityLimits: "",
  highestFloor: undefined,
};

/** Whether a location's building details hold anything: a No to the toggle
 *  asks first then, since it clears them. */
export function buildingHasContent(b: WcBuilding): boolean {
  return (
    b.bldNumber.trim() !== "" ||
    b.yearBuilt !== undefined ||
    b.occupiedArea !== undefined ||
    b.interest !== "" ||
    b.cityLimits !== "" ||
    b.highestFloor !== undefined
  );
}

/** The fields of a stored payroll row this step edits. */
const ROW_KEYS = new Set([
  "locationId",
  "codeId",
  "payroll",
  "fullTimeCount",
  "partTimeCount",
]);

export function newClassRow(key: string): WcClassRow {
  return {
    key,
    code: null,
    fullTime: undefined,
    partTime: undefined,
    payroll: undefined,
    carried: {},
  };
}

/** A blank location, with the one class-code row every location has. */
export function newLocation(key: string): WcLocation {
  return {
    key,
    address: { ...EMPTY_ADDRESS },
    hasBuildingDetails: null,
    building: { ...EMPTY_BUILDING },
    rows: [newClassRow(`${key}:row-1`)],
  };
}

const optional = (value: string | null | undefined) =>
  value ? value : undefined;

/** An address as the steps hold it, from one PMS sent. */
export function addressOf(a: {
  street: string;
  addressLine2?: string | null;
  city: string;
  state: string;
  zip: string;
  county?: string | null;
}): AddressValue {
  const addressLine2 = optional(a.addressLine2);
  const county = optional(a.county);
  return {
    street: a.street,
    ...(addressLine2 ? { addressLine2 } : {}),
    city: a.city,
    state: a.state,
    zip: a.zip,
    ...(county ? { county } : {}),
  };
}

function locationFromSaved(l: SubmissionLocation): WcLocation {
  const area = l.occupiedArea === null ? NaN : Number(l.occupiedArea);
  return {
    key: l.id,
    id: l.id,
    ...(l.sourceLocationId ? { sourceLocationId: l.sourceLocationId } : {}),
    ...(l.locNumber ? { locNumber: l.locNumber } : {}),
    address: addressOf(l),
    hasBuildingDetails: l.hasBuildingDetails,
    building: {
      bldNumber: l.bldNumber ?? "",
      yearBuilt: l.yearBuilt ?? undefined,
      occupiedArea: Number.isFinite(area) ? area : undefined,
      interest: l.interest ?? "",
      cityLimits: l.cityLimits ?? "",
      highestFloor: l.highestFloor ?? undefined,
    },
    rows: [],
  };
}

const numberOf = (value: unknown) =>
  typeof value === "number" && Number.isFinite(value) ? value : undefined;

/** A stored code id, read for display through PMS's references (PMS-S150);
 *  one PMS couldn't resolve shows its bare id rather than nothing. Its wage
 *  tier comes from the detail's rows, as the references carry none
 *  (PMS-S817, BRK-S394). */
function codeFrom(
  codeId: string,
  refs: Record<string, CodeDisplayRef>,
  wages: Readonly<Record<string, CodeWage>>,
): CodeOption {
  const ref = refs[codeId];
  return {
    id: codeId,
    code: ref?.code ?? codeId,
    typeName: "",
    friendlyName: null,
    description: ref?.description ?? null,
    ...wages[codeId],
  };
}

function rowFromWire(
  row: Record<string, unknown>,
  refs: Record<string, CodeDisplayRef>,
  wages: Readonly<Record<string, CodeWage>>,
  key: string,
  checkLocation: boolean,
): WcClassRow {
  return {
    key,
    code:
      typeof row.codeId === "string" ? codeFrom(row.codeId, refs, wages) : null,
    fullTime: numberOf(row.fullTimeCount),
    partTime: numberOf(row.partTimeCount),
    payroll: numberOf(row.payroll),
    carried: Object.fromEntries(
      Object.entries(row).filter(([k]) => !ROW_KEYS.has(k)),
    ),
    ...(checkLocation ? { checkLocation: "saved" as const } : {}),
  };
}

/** The stored payroll rows, whatever shape the bag holds. */
function storedRows(data: Record<string, unknown>): Record<string, unknown>[] {
  const rows = data.exposures;
  return Array.isArray(rows)
    ? rows.filter(
        (r): r is Record<string, unknown> =>
          typeof r === "object" && r !== null,
      )
    : [];
}

/**
 * The step's locations from what PMS holds: its locations, in order, each with
 * the payroll rows that name it. A row with no location, or one naming a
 * location that isn't the submission's, lands on location 1 marked to check.
 * There is always a location 1, and every location has a row.
 */
export function wcFromWire(
  saved: readonly SubmissionLocation[],
  data: Record<string, unknown>,
  refs: Record<string, CodeDisplayRef>,
  wages: Readonly<Record<string, CodeWage>> = {},
): WcLocation[] {
  const locations = saved.map(locationFromSaved);
  const byId = new Map(locations.map((l) => [l.id, l]));
  storedRows(data).forEach((row, i) => {
    const own =
      typeof row.locationId === "string" ? byId.get(row.locationId) : undefined;
    if (own) {
      own.rows.push(rowFromWire(row, refs, wages, `${own.key}:${i}`, false));
      return;
    }
    if (locations.length === 0) {
      const first = newLocation(FIRST_LOCATION_KEY);
      first.rows = [];
      locations.push(first);
    }
    locations[0].rows.push(
      rowFromWire(row, refs, wages, `${locations[0].key}:${i}`, true),
    );
  });
  if (locations.length === 0) locations.push(newLocation(FIRST_LOCATION_KEY));
  for (const l of locations) {
    if (l.rows.length === 0) l.rows.push(newClassRow(`${l.key}:row-1`));
  }
  return locations;
}

function buildingInput(b: WcBuilding): Partial<SubmissionLocationInput> {
  const bldNumber = b.bldNumber.trim();
  return {
    ...(bldNumber ? { bldNumber } : {}),
    ...(b.yearBuilt !== undefined ? { yearBuilt: b.yearBuilt } : {}),
    ...(b.occupiedArea !== undefined
      ? { occupiedArea: String(b.occupiedArea) }
      : {}),
    ...(b.interest ? { interest: b.interest } : {}),
    ...(b.cityLimits ? { cityLimits: b.cityLimits } : {}),
    ...(b.highestFloor !== undefined ? { highestFloor: b.highestFloor } : {}),
  };
}

/** The locations as written: the whole list, each entry restating its
 *  location. The building's fields go only while the toggle is Yes, so a No
 *  clears them on PMS too. */
export function wcToWire(
  locations: readonly WcLocation[],
): SubmissionLocationInput[] {
  return locations.map((l) => {
    const a = trimAddress(l.address);
    return {
      ...(l.id ? { id: l.id } : {}),
      ...(l.sourceLocationId ? { sourceLocationId: l.sourceLocationId } : {}),
      street: a.street,
      ...(a.addressLine2 ? { addressLine2: a.addressLine2 } : {}),
      city: a.city,
      state: a.state,
      zip: a.zip,
      ...(a.county ? { county: a.county } : {}),
      ...(l.locNumber ? { locNumber: l.locNumber } : {}),
      ...(l.hasBuildingDetails !== null
        ? { hasBuildingDetails: l.hasBuildingDetails }
        : {}),
      ...(l.hasBuildingDetails === true ? buildingInput(l.building) : {}),
    };
  });
}

/** The payroll rows as written, flat, each pointing at the location PMS kept
 *  for its location: `kept` is in the order the locations were sent. */
export function exposuresWire(
  locations: readonly WcLocation[],
  kept: readonly SubmissionLocation[],
): Record<string, unknown>[] {
  return locations.flatMap((l, i) =>
    l.rows.map((r) => ({
      ...r.carried,
      ...(kept[i] ? { locationId: kept[i].id } : {}),
      ...(r.code ? { codeId: r.code.id } : {}),
      ...(r.payroll !== undefined ? { payroll: r.payroll } : {}),
      ...(r.fullTime !== undefined ? { fullTimeCount: r.fullTime } : {}),
      ...(r.partTime !== undefined ? { partTimeCount: r.partTime } : {}),
    })),
  );
}

/** The saved locations this list leaves out. */
export function removedLocationIds(
  saved: readonly SubmissionLocation[],
  locations: readonly WcLocation[],
): Set<string> {
  const listed = new Set(locations.map((l) => l.id));
  return new Set(saved.map((s) => s.id).filter((id) => !listed.has(id)));
}

/** The stored payroll rows without those of the removed locations: written
 *  before the locations, so PMS can let those go (else 409 location_in_use). */
export function keptExposures(
  data: Record<string, unknown>,
  removed: ReadonlySet<string>,
): Record<string, unknown>[] {
  return storedRows(data).filter(
    (r) => !(typeof r.locationId === "string" && removed.has(r.locationId)),
  );
}

/** Whether the step is done: PMS holds payroll rows, each at a location. A
 *  row without one (saved before submissions had locations) still needs the
 *  broker to check it. */
export function locationsDone(data: Record<string, unknown>): boolean {
  const rows = storedRows(data);
  return rows.length > 0 && rows.every((r) => typeof r.locationId === "string");
}

/** The account's addresses a location can be copied from: its premises and
 *  its mailing address, each once, less those already copied in. */
export function locationCandidates(
  account: AccountDetail | null,
  locations: readonly WcLocation[],
): Address[] {
  if (!account) return [];
  const copied = new Set(locations.map((l) => l.sourceLocationId));
  const seen = new Set<string>();
  const out: Address[] = [];
  const addresses = [
    ...(account.physicalLocations ?? []),
    ...(account.mailingAddress ? [account.mailingAddress] : []),
  ];
  for (const a of addresses) {
    if (seen.has(a.id) || copied.has(a.id)) continue;
    seen.add(a.id);
    out.push(a);
  }
  return out;
}

/** Whether a location is untouched: not saved, nothing typed or chosen. An
 *  address added from the account takes its place rather than following it. */
export function isBlankLocation(l: WcLocation): boolean {
  const a = l.address;
  return (
    !l.id &&
    !l.sourceLocationId &&
    [a.street, a.addressLine2, a.city, a.state, a.zip, a.county].every(
      (part) => !part?.trim(),
    ) &&
    l.hasBuildingDetails === null &&
    l.rows.every(
      (r) =>
        !r.code &&
        r.fullTime === undefined &&
        r.partTime === undefined &&
        r.payroll === undefined,
    )
  );
}

/** An account address, copied into a new location with its source. */
export function locationFromAccount(a: Address, key: string): WcLocation {
  return { ...newLocation(key), sourceLocationId: a.id, address: addressOf(a) };
}

/** An account address in full, as the broker picks it. */
export function addressLabel(a: Address): string {
  return (
    a.formattedAddress ??
    [a.street, a.addressLine2, a.city, `${a.state} ${a.zip}`]
      .filter(Boolean)
      .join(", ")
  );
}

/** A location's employees: its rows' full-time and part-time, summed (Nav,
 *  2026-10-07). Read-only; it is Great American's `locationEmployeeCount`. */
export function employeesAt(location: WcLocation): number {
  return location.rows.reduce(
    (sum, r) => sum + (r.fullTime ?? 0) + (r.partTime ?? 0),
    0,
  );
}

/**
 * What a row's class-code search names (PMS-S665): the saved location, while
 * its state is the one PMS holds; else just the state, for a location not yet
 * saved or moved to another state. Nothing without a state.
 */
export function codeLocation(
  location: WcLocation,
  saved: readonly SubmissionLocation[],
): { id?: string; state: string } | undefined {
  const state = location.address.state.trim();
  if (!state) return undefined;
  const was = location.id ? saved.find((s) => s.id === location.id) : undefined;
  return was && was.state === state ? { id: location.id, state } : { state };
}

const wholeOrZero = (n: number) => Number.isInteger(n) && n >= 0;

function rowErrors(
  r: WcClassRow,
  at: string,
  errors: Record<string, string>,
): void {
  if (!r.code) errors[`${at}.code`] = "Choose the class code.";
  const count = (value: number | undefined, field: string, missing: string) => {
    if (value === undefined) errors[`${at}.${field}`] = missing;
    else if (!wholeOrZero(value)) {
      errors[`${at}.${field}`] = "Enter a whole number of 0 or more.";
    }
  };
  count(r.fullTime, "fullTime", "Enter the full-time employees (0 if none).");
  count(r.partTime, "partTime", "Enter the part-time employees (0 if none).");
  if (r.payroll === undefined) {
    errors[`${at}.payroll`] = "Enter the annual payroll.";
  } else if (r.payroll < 0) {
    errors[`${at}.payroll`] = "Enter an amount of 0 or more.";
  }
}

function buildingErrors(
  b: WcBuilding,
  at: string,
  errors: Record<string, string>,
): void {
  const { yearBuilt, highestFloor, occupiedArea } = b;
  if (
    yearBuilt !== undefined &&
    !(Number.isInteger(yearBuilt) && yearBuilt >= 1700 && yearBuilt <= 2100)
  ) {
    errors[`${at}.yearBuilt`] = "Enter a year from 1700 to 2100.";
  }
  if (
    highestFloor !== undefined &&
    !(
      Number.isInteger(highestFloor) &&
      highestFloor >= 1 &&
      highestFloor <= 200
    )
  ) {
    errors[`${at}.highestFloor`] = "Enter a floor from 1 to 200.";
  }
  if (occupiedArea !== undefined && occupiedArea < 0) {
    errors[`${at}.occupiedArea`] = "Enter an area of 0 or more.";
  }
}

/** An address's words for PMS's checks, part by part (BRK-S339): PMS names
 *  the part, so the address says which. `whose` is "" for a location's, and
 *  "mailing address’s " for the account's mailing address. */
function addressWords(at: string, whose: string): WizardRefusalWording {
  return {
    [`${at}.street`]: textWords(`Enter the ${whose}street.`, `${whose}street`),
    [`${at}.addressLine2`]: textWords(
      `Enter the ${whose}address line 2, or leave it blank.`,
      `${whose}address line 2`,
    ),
    [`${at}.city`]: textWords(`Enter the ${whose}city.`, `${whose}city`),
    [`${at}.state`]: oneWords(`Choose the ${whose}state.`),
    [`${at}.zip`]: {
      ...oneWords(`Check the ${whose}ZIP code.`),
      required: `Enter the ${whose}ZIP code.`,
      too_short: `Enter the ${whose}full ZIP code.`,
    },
    [`${at}.county`]: textWords(`Enter the ${whose}county.`, `${whose}county`),
  };
}

/** What PMS's checks of the step's writes refuse, in the step's words
 *  (BRK-S339): each location's address, and the mailing address the step
 *  writes on the account. The class codes' rows go in the field bag, which
 *  PMS checks with its own sentences. */
export const LOCATIONS_REFUSALS: WizardRefusalWording = {
  ...addressWords("locations.*", ""),
  ...addressWords("mailingAddress", "mailing address’s "),
};

/** The step's checks, keyed by where each shows: `locations.N.address`, a
 *  row's `locations.N.rows.M.<field>`, a building's `locations.N.<field>`, and
 *  `mailing` for a separate mailing address. A null `mailing` is one the step
 *  doesn't write (an insured on record), and building details a line doesn't
 *  ask are never checked: whatever a location holds stays as it is. */
export function validateLocations(
  locations: readonly WcLocation[],
  mailing: WcMailing | null,
  asks: LocationsAsks = { buildings: true },
): WizardFieldErrors {
  const errors: Record<string, string> = {};
  if (locations.length === 0) errors[ROOT] = "Add the first location.";
  locations.forEach((l, i) => {
    const at = `locations.${i}`;
    if (!isCompleteAddress(l.address)) {
      errors[`${at}.address`] = "Enter the street, city, state and ZIP.";
    } else if (!l.address.county?.trim()) {
      errors[`${at}.address`] = "Enter the county.";
    }
    l.rows.forEach((r, j) => rowErrors(r, `${at}.rows.${j}`, errors));
    if (asks.buildings && l.hasBuildingDetails === true) {
      buildingErrors(l.building, at, errors);
    }
  });
  if (
    mailing?.location === null &&
    !isCompleteAddress(mailing.mailing ?? { ...EMPTY_ADDRESS })
  ) {
    errors.mailing = "Enter the mailing address’s street, city, state and ZIP.";
  }
  return errors;
}

/** The location the mailing address is, by its key; location 1 when the
 *  key names none, as when the chosen one was removed. Null for a separate
 *  address. */
export function mailingLocation(
  locations: readonly WcLocation[],
  mailing: WcMailing,
): WcLocation | null {
  if (mailing.location === null) return null;
  return (
    locations.find((l) => l.key === mailing.location) ?? locations[0] ?? null
  );
}

/** The mailing address to save: the chosen location's, else the separate
 *  one, trimmed. */
export function mailingFor(
  locations: readonly WcLocation[],
  mailing: WcMailing,
): AddressValue {
  return trimAddress(
    mailing.location === null
      ? (mailing.mailing ?? EMPTY_ADDRESS)
      : (mailingLocation(locations, mailing)?.address ?? EMPTY_ADDRESS),
  );
}

/** The mailing select's options (BRK-S390): each location by its full
 *  address once it has one ("Location 2 — 450 Oak Ave, Oakland, CA 94607"),
 *  then a different address. */
export function mailingOptions(
  locations: readonly WcLocation[],
): { value: string; label: string }[] {
  return [
    ...locations.map((l, i) => ({
      value: l.key,
      label: isCompleteAddress(l.address)
        ? `Location ${i + 1} — ${formatAddress(trimAddress(l.address))}`
        : `Location ${i + 1}`,
    })),
    { value: MAILING_ELSEWHERE, label: "Enter a different address" },
  ];
}

const sameText = (a: string | undefined, b: string | undefined) =>
  (a ?? "").trim().toLowerCase() === (b ?? "").trim().toLowerCase();

/** Whether two addresses are the same place, county aside. */
export function sameAddress(a: AddressValue, b: AddressValue): boolean {
  return (
    sameText(a.street, b.street) &&
    sameText(a.addressLine2, b.addressLine2) &&
    sameText(a.city, b.city) &&
    sameText(a.state, b.state) &&
    sameText(a.zip, b.zip)
  );
}

/** Whether the account's mailing address differs from `mailing`, county
 *  included: an unchanged one isn't written again. */
export function mailingChanged(
  account: AccountDetail,
  mailing: AddressValue,
): boolean {
  const held = account.mailingAddress;
  if (!held) return true;
  const current = addressOf(held);
  return (
    !sameAddress(current, mailing) ||
    (current.county ?? "") !== (mailing.county ?? "")
  );
}

/** The mailing choice as the step opens (BRK-S390): location 1's when the
 *  account has none yet; the entered location it matches, whichever it is;
 *  else the account's, as a different address, filled in. */
export function mailingFrom(
  account: AccountDetail | null,
  locations: readonly WcLocation[],
): WcMailing {
  const held = account?.mailingAddress;
  if (!held) return { location: locations[0]?.key ?? FIRST_LOCATION_KEY };
  const current = addressOf(held);
  const match = locations.find((l) => sameAddress(current, l.address));
  return match ? { location: match.key } : { location: null, mailing: current };
}
